import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PopoverPortalProps {
  /** The element the popover is anchored to (usually the wrapper holding the trigger button). */
  anchorRef: React.RefObject<HTMLElement>;
  /** Whether the popover is shown. */
  open: boolean;
  /** Called when the popover should close (outside click, Escape). */
  onClose: () => void;
  /** Align the popover's left edge ('start', default) or right edge ('end') to the anchor. */
  align?: 'start' | 'end';
  /** Match the popover width to the anchor's width (replaces full-width menus). */
  matchAnchorWidth?: boolean;
  /** Explicit popover width in px (e.g. 288 = w-72, 256 = w-64, 224 = w-56). */
  width?: number;
  /** Gap in px between the anchor and the popover (default 4, ~mt-1). */
  gap?: number;
  /** Styling for the popover container (bg/border/rounded/shadow/scroll, as the old menu div had). */
  className?: string;
  /** Optional ref to the popover container (e.g. for scroll-into-view of focused items). */
  innerRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

interface Coords {
  top: number;
  left: number;
  width?: number;
}

const VIEWPORT_MARGIN = 8;

/**
 * Renders its children into `document.body` as a `position: fixed` popover anchored to
 * `anchorRef`. Because it lives outside the anchor's scroll container, it never extends
 * that container's scrollable area (no scrollbar-induced content shift) and is never
 * clipped by `overflow` ancestors. Repositions on scroll/resize, flips above the anchor
 * when there isn't room below, clamps horizontally to the viewport, and handles
 * outside-click + Escape to close.
 */
export const PopoverPortal: React.FC<PopoverPortalProps> = ({
  anchorRef,
  open,
  onClose,
  align = 'start',
  matchAnchorWidth = false,
  width,
  gap = 4,
  className,
  innerRef,
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      popoverRef.current = node;
      if (innerRef) (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [innerRef],
  );

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const resolvedWidth = matchAnchorWidth
      ? rect.width
      : typeof width === 'number'
        ? width
        : popoverRef.current?.offsetWidth ?? rect.width;
    const height = popoverRef.current?.offsetHeight ?? 0;

    let left = align === 'end' ? rect.right - resolvedWidth : rect.left;
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, vw - resolvedWidth - VIEWPORT_MARGIN));

    let top = rect.bottom + gap;
    // Flip above the anchor when the popover would overflow the bottom but fits above.
    if (height && top + height > vh - VIEWPORT_MARGIN && rect.top - gap - height >= VIEWPORT_MARGIN) {
      top = rect.top - gap - height;
    }

    setCoords({
      top,
      left,
      width: matchAnchorWidth || typeof width === 'number' ? resolvedWidth : undefined,
    });
  }, [anchorRef, align, gap, matchAnchorWidth, width]);

  // Position once the popover is mounted (and reset when closed). Runs in a layout effect
  // so measurement + placement happens before paint; `coords === null` keeps it hidden
  // until positioned to avoid a flash at the top-left corner.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
  }, [open, reposition]);

  // Keep the popover anchored while the page or any ancestor scroll container scrolls.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(reposition);
    };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [open, reposition]);

  // Outside-click + Escape. The popover lives outside the anchor's subtree, so we must
  // treat clicks inside either the anchor or the popover as "inside".
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={setRefs}
      style={{
        position: 'fixed',
        top: coords?.top ?? 0,
        left: coords?.left ?? 0,
        width: coords?.width,
        zIndex: 1000,
        visibility: coords ? 'visible' : 'hidden',
      }}
      className={className}
    >
      {children}
    </div>,
    document.body,
  );
};

export default PopoverPortal;
